import { memo, useMemo } from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Collection,
    ExpandButton,
    Heading,
    HeadlessTree,
    HeadlessTreeItem,
    HeadlessTreeItemContent,
    renderIcon,
    Separator,
} from '@gitbook/spine-react';

import { SiteOverviewSectionHeader, SiteOverviewSectionLink } from '../../../Components';
import { type AnySiteItem, getSiteTreeItem, type SiteCapability } from '../../../state';

interface SiteStructureOverviewProps {
    site: api.Site;
    sitePermissions: SiteCapability;
    siteStructure: api.SiteStructure;
    organization: api.Organization;
}

export function SiteStructureOverview(props: SiteStructureOverviewProps) {
    const { site, siteStructure, organization, sitePermissions } = props;
    const siteItem = useMemo(
        () => getSiteTreeItem({ site, structure: siteStructure }, { hideEmptySectionGroups: true }),
        [site, siteStructure]
    );
    if (siteItem.children.length === 0) {
        return null;
    }
    return (
        <>
            <Separator className="m-0" />
            <div className="flex flex-col gap-2 px-4">
                <SiteOverviewSectionHeader>
                    <Heading>Site structure</Heading>
                    {sitePermissions.canAdminSite ? (
                        <SiteOverviewSectionLink
                            href={{
                                screen: {
                                    type: 'site',
                                    organization: organization.id,
                                    site: site.id,
                                    tab: { type: 'settings', section: 'structure' },
                                },
                            }}
                        >
                            Manage
                        </SiteOverviewSectionLink>
                    ) : null}
                </SiteOverviewSectionHeader>
                <div>
                    <HeadlessTree<AnySiteItem>
                        // Temporary workaround for a bug when switching between sites
                        key={siteItem.id}
                        items={siteItem.children}
                        aria-label="Site structure"
                    >
                        {function renderItem(treeItem) {
                            const isExpandable =
                                'children' in treeItem && treeItem.children.length > 0;
                            const href = 'href' in treeItem ? treeItem.href : undefined;
                            return (
                                <HeadlessTreeItem
                                    href={href}
                                    textValue={treeItem.title}
                                    className="group my-px cursor-pointer rounded pl-[calc((var(--tree-item-level)-1)*0.75rem)] hover:bg-side"
                                    {...props}
                                >
                                    <TreeItemContent value={treeItem} isExpandable={isExpandable} />
                                    {'children' in treeItem ? (
                                        <Collection items={treeItem.children}>
                                            {renderItem}
                                        </Collection>
                                    ) : null}
                                </HeadlessTreeItem>
                            );
                        }}
                    </HeadlessTree>
                </div>
            </div>
        </>
    );
}

const TreeItemContent = memo(function TreeItemContent(props: {
    value: AnySiteItem;
    isExpandable: boolean;
}) {
    const { value, isExpandable } = props;
    const href = 'href' in value ? value.href : undefined;
    return (
        <HeadlessTreeItemContent>
            <div className="flex h-9 items-center gap-2 px-3">
                {renderIcon(value.icon, { className: 'size-3 shrink-0' })}
                <div className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
                    <span className="truncate">{value.title}</span>
                    {isExpandable && <ExpandButton />}
                </div>
                {href ? (
                    <span className="w-0 overflow-clip whitespace-nowrap opacity-0 group-hover:w-auto group-hover:opacity-100">
                        Go to space <Icon.ArrowRight />
                    </span>
                ) : null}
            </div>
        </HeadlessTreeItemContent>
    );
});
